import { useRestApiProvider } from "@jugl-web/rest-api";
import {
  DetailedTask,
  TasksApiTag,
  TasksSource,
  adaptDetailedTaskToPreviewTask,
  previewTasksAdapter,
} from "@jugl-web/rest-api/tasks";
import { TASK_COMMENTS_TAGS_CREATORS } from "@jugl-web/rest-api/tasks-comments";
import {
  DetailedTaskTemplate,
  adaptDetailedTemplateToPreviewTemplate,
  previewTasksTemplatesAdapter,
} from "@jugl-web/rest-api/tasks-templates";
import { HookOutOfContextError, assert } from "@jugl-web/utils";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useDispatch } from "react-redux";
import { Observable, Subject, filter } from "rxjs";
import { LiveUpdateEvent } from "../../../live-updates";
import { MY_TASKS_SOURCE } from "../../consts";
import {
  SubtaskAttachmentLiveUpdateDeletedEvent,
  SubtaskAttachmentLiveUpdateEvent,
  SubtaskLiveUpdateEvent,
  TaskAttachmentLiveUpdateCreatedEvent,
  TaskAttachmentLiveUpdateDeletedEvent,
  TaskAttachmentLiveUpdateEvent,
  TaskClientFeedbackLiveUpdateEvent,
  TaskCommentLiveUpdateEvent,
  TaskLiveUpdateArchivedEvent,
  TaskLiveUpdateCreatedEvent,
  TaskLiveUpdateDeletedEvent,
  TaskLiveUpdateEvent,
  TaskLiveUpdateRestoredEvent,
} from "./types";
import {
  isSubtaskAttachmentCreatedEvent,
  isSubtaskAttachmentDeletedEvent,
  isSubtaskAttachmentLiveUpdateEvent,
  isSubtaskAttachmentRenamedEvent,
  isSubtaskCreatedEvent,
  isSubtaskDeletedEvent,
  isSubtaskLiveUpdateEvent,
  isSubtaskMovedEvent,
  isSubtaskUpdatedEvent,
  isTaskArchivedEvent,
  isTaskAttachmentCreatedEvent,
  isTaskAttachmentDeletedEvent,
  isTaskAttachmentLiveUpdateEvent,
  isTaskAttachmentRenamedEvent,
  isTaskClientFeedbackCreatedEvent,
  isTaskClientFeedbackLiveUpdateEvent,
  isTaskCommentCreatedEvent,
  isTaskCommentDeletedEvent,
  isTaskCommentLiveUpdateEvent,
  isTaskCommentUpdatedEvent,
  isTaskCreatedEvent,
  isTaskDeletedEvent,
  isTaskLiveUpdateEvent,
  isTaskRestoredEvent,
  isTaskUpdatedEvent,
} from "./utils";

type TemplatesResponse = { isTemplate: true; template: DetailedTaskTemplate };
type TasksResponse = { isTemplate: false; task: DetailedTask };

type FetchTaskOrTemplateHandler = (args: {
  entityId: string;
  taskOrTemplateId: string;
  noCache?: true;
}) => Promise<TemplatesResponse | TasksResponse>;

export interface TaskLiveUpdatesManagerProps {
  children: ReactNode;
  events$: Subject<LiveUpdateEvent>;
  meId: string | undefined;
  isInTaskDetailsView: boolean;
}

interface FetchedTaskPayload {
  task: DetailedTask;
  reason: "created" | "updated" | "restored";
}

interface TaskLiveUpdateContextValue {
  taskEvents$: Observable<TaskLiveUpdateEvent>;
  fetchedTask$: Observable<FetchedTaskPayload>;
  taskAttachmentEvents$: Observable<TaskAttachmentLiveUpdateEvent>;
  taskCommentEvents$: Observable<TaskCommentLiveUpdateEvent>;
  taskClientFeedbackEvents$: Observable<TaskClientFeedbackLiveUpdateEvent>;
  subtaskEvents$: Observable<SubtaskLiveUpdateEvent>;
  subtaskAttachmentEvents$: Observable<SubtaskAttachmentLiveUpdateEvent>;
}

const TaskLiveUpdateContext = createContext<TaskLiveUpdateContextValue | null>(
  null
);

export const TaskLiveUpdatesProvider: FC<TaskLiveUpdatesManagerProps> = ({
  children,
  events$,
  isInTaskDetailsView,
  meId,
}) => {
  const {
    moduleNotificationsApi,
    tasksApi,
    tasksCommentsApi,
    tasksTemplatesApi,
  } = useRestApiProvider();

  const [getTask] = tasksApi.useLazyGetTaskQuery();

  // #region Live updates streams
  const taskEvents$ = useMemo(
    () => events$.pipe(filter(isTaskLiveUpdateEvent)),
    [events$]
  );

  const taskAttachmentEvents$ = useMemo(
    () => events$.pipe(filter(isTaskAttachmentLiveUpdateEvent)),
    [events$]
  );

  const taskCommentEvents$ = useMemo(
    () => events$.pipe(filter(isTaskCommentLiveUpdateEvent)),
    [events$]
  );

  const taskClientFeedbackEvents$ = useMemo(
    () => events$.pipe(filter(isTaskClientFeedbackLiveUpdateEvent)),
    [events$]
  );

  const subtaskEvents$ = useMemo(
    () => events$.pipe(filter(isSubtaskLiveUpdateEvent)),
    [events$]
  );

  const subtaskAttachmentEvents$ = useMemo(
    () => events$.pipe(filter(isSubtaskAttachmentLiveUpdateEvent)),
    [events$]
  );
  // #endregion

  // #region Internal streams
  const fetchedTask$ = useMemo(() => new Subject<FetchedTaskPayload>(), []);
  // #endregion

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<ThunkDispatch<any, unknown, AnyAction>>();

  // #region Utils
  const fetchTaskOrTemplate = useCallback<FetchTaskOrTemplateHandler>(
    async ({ entityId, taskOrTemplateId, noCache }) => {
      const response = await getTask({
        entityId,
        taskId: taskOrTemplateId,
        noCache,
      });

      if (!response.data) {
        throw new Error("Couldn't fetch task or template");
      }

      if (response.data.type === "template") {
        return {
          isTemplate: true,
          template: response.data as unknown as DetailedTaskTemplate,
        };
      }

      return {
        isTemplate: false,
        task: response.data,
      };
    },
    [getTask]
  );
  // #endregion

  // #region Task live updates handlers
  const handleTaskCreatedEvent = useCallback(
    async (event: TaskLiveUpdateCreatedEvent) => {
      const { id, entity_id: entityId } = event.data;

      try {
        const response = await fetchTaskOrTemplate({
          entityId,
          taskOrTemplateId: id,
        });

        if (response.isTemplate) {
          const addTemplateToCollectionAction =
            tasksTemplatesApi.util.updateQueryData(
              "getTemplates",
              { entityId },
              (state) => {
                previewTasksTemplatesAdapter.addOne(
                  state,
                  adaptDetailedTemplateToPreviewTemplate(response.template)
                );
              }
            );

          dispatch(addTemplateToCollectionAction);
          return;
        }

        // Notify the internal stream that a task was created
        fetchedTask$.next({ task: response.task, reason: "created" });

        // Collect all the collections in which we know for sure
        // that the task should be present after creation
        const potentialSourcesToUpdate: (TasksSource | null)[] = [
          { type: "boardTasks", boardId: "my" },
          // If the task belongs to a customer, update the customer tasks collection
          response.task.cust_id
            ? { type: "customerTasks", customerId: response.task.cust_id }
            : null,
          // If the task belongs to a board, update the board tasks collection
          response.task.board_id
            ? { type: "boardTasks", boardId: response.task.board_id }
            : null,
        ];

        const relevantSourcesToUpdate = potentialSourcesToUpdate.filter(
          (source): source is TasksSource => Boolean(source)
        );

        const addTaskToCollectionActions = relevantSourcesToUpdate.map(
          (source) =>
            tasksApi.util.updateQueryData(
              "getTasks",
              { entityId, source },
              (state) => {
                previewTasksAdapter.upsertOne(
                  state,
                  adaptDetailedTaskToPreviewTask(response.task)
                );
              }
            )
        );

        addTaskToCollectionActions.forEach((action) => dispatch(action));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    [
      dispatch,
      fetchTaskOrTemplate,
      fetchedTask$,
      tasksApi.util,
      tasksTemplatesApi.util,
    ]
  );

  const handleTaskRestoredEvent = useCallback(
    async (event: TaskLiveUpdateRestoredEvent) => {
      const { id, entity_id: entityId } = event.data;

      try {
        const response = await fetchTaskOrTemplate({
          entityId,
          taskOrTemplateId: id,
        });

        // This should never happen, but just in case skip the update if the response is a template
        if (response.isTemplate) {
          return;
        }

        // Notify the internal stream that a task was restored
        fetchedTask$.next({ task: response.task, reason: "restored" });

        const addTaskToCollectionAction = tasksApi.util.updateQueryData(
          "getTasks",
          { entityId, source: MY_TASKS_SOURCE },
          (state) => {
            previewTasksAdapter.addOne(
              state,
              adaptDetailedTaskToPreviewTask(response.task)
            );
          }
        );

        dispatch(addTaskToCollectionAction);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    [dispatch, fetchTaskOrTemplate, fetchedTask$, tasksApi.util]
  );

  /**
   * Handles task updated event.
   *
   * @param taskOrTemplateId - The ID of the task or template.
   * @param entityId - The ID of the entity.
   * @param forceTaskCacheUpdate - Bypass incoming data freshness check and force task cache update.
   */
  const handleTaskUpdatedEvent = useCallback(
    async (
      taskOrTemplateId: string,
      entityId: string,
      forceTaskCacheUpdate = false
    ) => {
      try {
        const response = await fetchTaskOrTemplate({
          entityId,
          taskOrTemplateId,
          noCache: true,
        });

        if (response.isTemplate) {
          const updatePreviewTemplateAction =
            tasksTemplatesApi.util.updateQueryData(
              "getTemplates",
              { entityId },
              (state) => {
                previewTasksTemplatesAdapter.updateOne(state, {
                  id: response.template.id,
                  changes: adaptDetailedTemplateToPreviewTemplate(
                    response.template
                  ),
                });
              }
            );

          const updateDetailedTemplateAction =
            tasksTemplatesApi.util.updateQueryData(
              "getTemplate",
              { entityId, templateId: response.template.id },
              (state) => {
                Object.assign(state, response.template);
              }
            );

          dispatch(updatePreviewTemplateAction);
          dispatch(updateDetailedTemplateAction);
          return;
        }

        // Notify the internal stream that a task was updated
        fetchedTask$.next({ task: response.task, reason: "updated" });

        const updateDetailedTaskAction = tasksApi.util.updateQueryData(
          "getTask",
          { entityId, taskId: response.task.id },
          (state) => {
            Object.assign(state, response.task);
          }
        );

        // Collect all the collections that might have the task
        const potentialSourcesToUpdate: (TasksSource | null)[] = [
          { type: "boardTasks", boardId: "my" },
          { type: "boardTasks", boardId: "team" },
          // If the task belongs to a customer, update the customer tasks collection
          response.task.cust_id
            ? { type: "customerTasks", customerId: response.task.cust_id }
            : null,
          // If the task belongs to a board, update the board tasks collection
          response.task.board_id
            ? { type: "boardTasks", boardId: response.task.board_id }
            : null,
        ];

        const relevantSourcesToUpdate = potentialSourcesToUpdate.filter(
          (source): source is TasksSource => Boolean(source)
        );

        const updatePreviewTaskActions = relevantSourcesToUpdate.map((source) =>
          tasksApi.util.updateQueryData(
            "getTasks",
            { entityId, source },
            (state) => {
              const taskToUpdate = state.entities[response.task.id];

              // If the task was updated locally before the request was completed,
              // we don't need to update it as the local changes might be more actual
              const isIncomingTaskOutdated =
                taskToUpdate &&
                taskToUpdate.updated_at >= response.task.updated_at;

              if (isIncomingTaskOutdated && !forceTaskCacheUpdate) {
                return;
              }

              previewTasksAdapter.updateOne(state, {
                id: response.task.id,
                changes: adaptDetailedTaskToPreviewTask(response.task),
              });
            }
          )
        );

        updatePreviewTaskActions.forEach((action) => dispatch(action));
        dispatch(updateDetailedTaskAction);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    [
      dispatch,
      fetchTaskOrTemplate,
      fetchedTask$,
      tasksApi.util,
      tasksTemplatesApi.util,
    ]
  );

  const handleTaskDeletedEvent = useCallback(
    (event: TaskLiveUpdateDeletedEvent | TaskLiveUpdateArchivedEvent) => {
      const { id, entity_id: entityId } = event.data;

      const removeTaskFromCollectionAction = tasksApi.util.updateQueryData(
        "getTasks",
        { entityId, source: MY_TASKS_SOURCE },
        (state) => {
          previewTasksAdapter.removeOne(state, id);
        }
      );

      const removeTemplateFromCollectionAction =
        tasksTemplatesApi.util.updateQueryData(
          "getTemplates",
          { entityId },
          (state) => {
            previewTasksTemplatesAdapter.removeOne(state, id);
          }
        );

      dispatch(
        tasksApi.util.updateQueryData(
          "getTask",
          { entityId, taskId: id },
          (state) => {
            Object.assign<DetailedTask, Partial<DetailedTask>>(state, {
              _is_deleted: true,
            });
          }
        )
      );

      // At this point, we don't know if the event refers to a task or a template,
      // so we attempt to remove it from both collections, just in case
      dispatch(removeTaskFromCollectionAction);
      dispatch(removeTemplateFromCollectionAction);
    },
    [dispatch, tasksApi.util, tasksTemplatesApi.util]
  );

  // Every time a task live update arrives, we manually mark notifications
  // as unread, triggering the red indicator without the need to send requests
  // to the backend
  const markNotificationsAsUnread = useCallback(
    (entityId: string, updateProducerId: string) => {
      assert(!!meId);

      const isSelfUpdate = updateProducerId === meId;

      if (isSelfUpdate) {
        return;
      }

      const taskNotificationsUnreadIndicatorUpdateAction =
        moduleNotificationsApi.util.updateQueryData(
          "getModuleNotificationsUnreadIndicator",
          { entityId, module: "task" },
          () => ({ unread: true })
        );

      dispatch(taskNotificationsUnreadIndicatorUpdateAction);
    },
    [dispatch, meId, moduleNotificationsApi.util]
  );

  const handleTaskLiveUpdateEvent = useCallback(
    (event: TaskLiveUpdateEvent<string, object>) => {
      if (isTaskCreatedEvent(event)) {
        handleTaskCreatedEvent(event);
        markNotificationsAsUnread(event.data.entity_id, event.action_by);
        return;
      }

      if (isTaskRestoredEvent(event)) {
        handleTaskRestoredEvent(event);
        markNotificationsAsUnread(event.data.entity_id, event.action_by);
      }

      if (isTaskUpdatedEvent(event)) {
        const isSyntheticEvent = event.action_by === "FAKE";

        handleTaskUpdatedEvent(
          event.data.id,
          event.data.entity_id,
          isSyntheticEvent
        );

        if (!isSyntheticEvent) {
          markNotificationsAsUnread(event.data.entity_id, event.action_by);
        }

        return;
      }

      if (isTaskDeletedEvent(event) || isTaskArchivedEvent(event)) {
        handleTaskDeletedEvent(event);
        markNotificationsAsUnread(event.data.entity_id, event.action_by);
        return;
      }

      // eslint-disable-next-line no-console
      console.warn(
        `Unhandled task live update event action: ${event.action}`,
        event
      );
    },
    [
      handleTaskCreatedEvent,
      markNotificationsAsUnread,
      handleTaskRestoredEvent,
      handleTaskUpdatedEvent,
      handleTaskDeletedEvent,
    ]
  );
  // #endregion

  // #region Task attachment live updates handlers
  const updateTaskAttachmentsCountInGetTaskQuery = useCallback(
    (
      entityId: string,
      taskId: string,
      updateFn: (previousCount: number) => number
    ) => {
      const action = tasksApi.util.updateQueryData(
        "getTask",
        { entityId, taskId },
        (draftState) => {
          draftState.attachment_count = updateFn(draftState.attachment_count);
        }
      );

      dispatch(action);
    },
    [dispatch, tasksApi.util]
  );

  const handleTaskAttachmentCreatedEvent = useCallback(
    (event: TaskAttachmentLiveUpdateCreatedEvent) => {
      // Refresh attachments state by invalidating cache tag
      const invalidateTagsAction = tasksApi.util.invalidateTags([
        TasksApiTag.taskAttachments,
      ]);

      dispatch(invalidateTagsAction);

      // Bump attachments count in the cached task object
      updateTaskAttachmentsCountInGetTaskQuery(
        event.data.entity_id,
        event.data.task_id,
        (previousCount) => previousCount + 1
      );
    },
    [dispatch, tasksApi.util, updateTaskAttachmentsCountInGetTaskQuery]
  );

  const handleTaskAttachmentRenamedEvent = useCallback(() => {
    // Refresh attachments state by invalidating cache tag
    const invalidateTagsAction = tasksApi.util.invalidateTags([
      TasksApiTag.taskAttachments,
    ]);

    dispatch(invalidateTagsAction);
  }, [dispatch, tasksApi.util]);

  const handleTaskAttachmentDeletedEvent = useCallback(
    (event: TaskAttachmentLiveUpdateDeletedEvent) => {
      const { data } = event;

      // Remove the attachment from the cached task attachments list
      const updateTaskAttachmentsAction = tasksApi.util.updateQueryData(
        "getTaskAttachments",
        {
          entityId: data.entity_id,
          taskId: data.task_id,
        },
        (draftState) => {
          const indexToDelete = draftState.findIndex(
            (attachment) => attachment.name === data.file_name
          );

          if (indexToDelete === -1) {
            return;
          }

          draftState.splice(indexToDelete, 1);
        }
      );

      dispatch(updateTaskAttachmentsAction);

      // Decrement attachments count in the cached task object
      updateTaskAttachmentsCountInGetTaskQuery(
        data.entity_id,
        data.task_id,
        (previousCount) => previousCount - 1
      );
    },
    [dispatch, tasksApi.util, updateTaskAttachmentsCountInGetTaskQuery]
  );

  const handleTaskAttachmentLiveUpdateEvent = useCallback(
    (event: TaskAttachmentLiveUpdateEvent) => {
      if (isTaskAttachmentCreatedEvent(event)) {
        handleTaskAttachmentCreatedEvent(event);
        return;
      }

      if (isTaskAttachmentRenamedEvent(event)) {
        handleTaskAttachmentRenamedEvent();
        return;
      }

      if (isTaskAttachmentDeletedEvent(event)) {
        handleTaskAttachmentDeletedEvent(event);
      }
    },
    [
      handleTaskAttachmentCreatedEvent,
      handleTaskAttachmentDeletedEvent,
      handleTaskAttachmentRenamedEvent,
    ]
  );
  // #endregion

  // #region Task comment live updates handlers
  const handleTaskCommentLiveUpdateEvent = useCallback(
    (event: TaskCommentLiveUpdateEvent) => {
      if (isInTaskDetailsView || !isTaskCommentLiveUpdateEvent(event)) {
        return;
      }

      if (isTaskCommentCreatedEvent(event)) {
        handleTaskUpdatedEvent(event.data.task_id, event.data.entity_id);
      }

      if (
        isTaskCommentCreatedEvent(event) ||
        isTaskCommentUpdatedEvent(event) ||
        isTaskCommentDeletedEvent(event)
      ) {
        const invalidateTaskCommentsTagsAction =
          tasksCommentsApi.util.invalidateTags([
            TASK_COMMENTS_TAGS_CREATORS.TASK_ID(event.data.task_id),
          ]);

        dispatch(invalidateTaskCommentsTagsAction);
      }
    },
    [
      dispatch,
      isInTaskDetailsView,
      tasksCommentsApi.util,
      handleTaskUpdatedEvent,
    ]
  );
  // #endregion

  // #region Task client feedback live updates handlers
  const handleTaskClientFeedbackLiveUpdateEvent = useCallback(
    (event: TaskClientFeedbackLiveUpdateEvent) => {
      if (isInTaskDetailsView || !isTaskClientFeedbackCreatedEvent(event)) {
        return;
      }

      handleTaskUpdatedEvent(event.data.task_id, event.data.entity_id);
    },
    [isInTaskDetailsView, handleTaskUpdatedEvent]
  );
  // #endregion

  // #region Subtask live updates handlers
  const handleSubtaskLiveUpdateEvent = useCallback(
    (event: SubtaskLiveUpdateEvent) => {
      if (isSubtaskCreatedEvent(event)) {
        handleTaskUpdatedEvent(event.data.task_id, event.data.entity_id);
        return;
      }

      if (isSubtaskUpdatedEvent(event)) {
        handleTaskUpdatedEvent(event.data.task_id, event.data.entity_id);
        return;
      }

      if (isSubtaskDeletedEvent(event)) {
        handleTaskUpdatedEvent(event.data.task_id, event.data.entity_id);
        return;
      }

      if (isSubtaskMovedEvent(event)) {
        handleTaskUpdatedEvent(event.data.src_task_id, event.data.entity_id);
        handleTaskUpdatedEvent(event.data.dest_task_id, event.data.entity_id);
      }
    },
    [handleTaskUpdatedEvent]
  );
  // #endregion

  // #region Subtask attachment live updates handlers
  const handleSubtaskAttachmentCreatedEvent = useCallback(() => {
    const action = tasksApi.util.invalidateTags([
      TasksApiTag.subtaskAttachments,
    ]);

    dispatch(action);
  }, [dispatch, tasksApi.util]);

  const handleSubtaskAttachmentRenamedEvent = useCallback(() => {
    const action = tasksApi.util.invalidateTags([
      TasksApiTag.subtaskAttachments,
    ]);

    dispatch(action);
  }, [dispatch, tasksApi.util]);

  const handleSubtaskAttachmentDeletedEvent = useCallback(
    (event: SubtaskAttachmentLiveUpdateDeletedEvent) => {
      const { data } = event;

      const action = tasksApi.util.updateQueryData(
        "getTaskAttachments",
        {
          entityId: data.entity_id,
          taskId: data.task_id,
          subtaskId: data.subtask_id,
        },
        (draftState) => {
          const indexToDelete = draftState.findIndex(
            (attachment) => attachment.name === data.file_name
          );

          if (indexToDelete === -1) {
            return;
          }

          draftState.splice(indexToDelete, 1);
        }
      );

      dispatch(action);
    },
    [dispatch, tasksApi.util]
  );

  const handleSubtaskAttachmentLiveUpdateEvent = useCallback(
    (event: SubtaskAttachmentLiveUpdateEvent) => {
      if (isSubtaskAttachmentCreatedEvent(event)) {
        handleSubtaskAttachmentCreatedEvent();
        return;
      }

      if (isSubtaskAttachmentRenamedEvent(event)) {
        handleSubtaskAttachmentRenamedEvent();
        return;
      }

      if (isSubtaskAttachmentDeletedEvent(event)) {
        handleSubtaskAttachmentDeletedEvent(event);
      }
    },
    [
      handleSubtaskAttachmentCreatedEvent,
      handleSubtaskAttachmentRenamedEvent,
      handleSubtaskAttachmentDeletedEvent,
    ]
  );
  // #endregion

  // Subscribe to all live update event modules
  useEffect(() => {
    const taskEventsSubscription = taskEvents$.subscribe(
      handleTaskLiveUpdateEvent
    );
    const taskAttachmentEventsSubscription = taskAttachmentEvents$.subscribe(
      handleTaskAttachmentLiveUpdateEvent
    );
    const taskCommentEventsSubscription = taskCommentEvents$.subscribe(
      handleTaskCommentLiveUpdateEvent
    );
    const clientFeedbackSubscription = taskClientFeedbackEvents$.subscribe(
      handleTaskClientFeedbackLiveUpdateEvent
    );
    const subtaskEventsSubscription = subtaskEvents$.subscribe(
      handleSubtaskLiveUpdateEvent
    );
    const subtaskAttachmentEventsSubscription =
      subtaskAttachmentEvents$.subscribe(
        handleSubtaskAttachmentLiveUpdateEvent
      );

    return () => {
      taskEventsSubscription.unsubscribe();
      taskAttachmentEventsSubscription.unsubscribe();
      taskCommentEventsSubscription.unsubscribe();
      clientFeedbackSubscription.unsubscribe();
      subtaskEventsSubscription.unsubscribe();
      subtaskAttachmentEventsSubscription.unsubscribe();
    };
  }, [
    handleSubtaskAttachmentLiveUpdateEvent,
    handleTaskAttachmentLiveUpdateEvent,
    handleSubtaskLiveUpdateEvent,
    handleTaskClientFeedbackLiveUpdateEvent,
    handleTaskCommentLiveUpdateEvent,
    handleTaskLiveUpdateEvent,
    subtaskAttachmentEvents$,
    subtaskEvents$,
    taskAttachmentEvents$,
    taskClientFeedbackEvents$,
    taskCommentEvents$,
    taskEvents$,
  ]);

  const contextValue = useMemo<TaskLiveUpdateContextValue>(
    () => ({
      taskEvents$,
      fetchedTask$,
      taskAttachmentEvents$,
      taskCommentEvents$,
      taskClientFeedbackEvents$,
      subtaskEvents$,
      subtaskAttachmentEvents$,
    }),
    [
      taskEvents$,
      fetchedTask$,
      taskAttachmentEvents$,
      taskCommentEvents$,
      taskClientFeedbackEvents$,
      subtaskEvents$,
      subtaskAttachmentEvents$,
    ]
  );

  return (
    <TaskLiveUpdateContext.Provider value={contextValue}>
      {children}
    </TaskLiveUpdateContext.Provider>
  );
};

export const useTaskLiveUpdatesContext = () => {
  const context = useContext(TaskLiveUpdateContext);

  if (!context) {
    throw new HookOutOfContextError(
      "useTaskLiveUpdatesContext",
      "TaskLiveUpdateContext"
    );
  }

  return context;
};
